import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { apiClient } from "../lib/api";

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Merhaba! Ben Gensan Motors'un yapay zeka asistanıyım. Motorlar, fiyatlar ve hizmetlerimiz hakkında sorularınızı yanıtlayabilirim. Size nasıl yardımcı olabilirim?",
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((prev) => [...prev, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const r = await apiClient.post("/chat", { message: text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((prev) => [...prev, { role: "bot", text: r.data.reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Şu an bağlantı sorunu yaşıyorum. Lütfen birazdan tekrar dener misiniz?" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-[60] w-16 h-16 flex items-center justify-center shadow-2xl transition-all duration-300 ${
          open ? "bg-zinc-900 rotate-90" : "bg-orange-600 hover:bg-orange-700"
        }`}
        aria-label="Chatbot aç"
      >
        {open ? <X size={26} className="text-white" /> : <MessageCircle size={26} className="text-white" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full">
            <span className="absolute inset-0 bg-orange-500 rounded-full animate-ping opacity-75" />
            <span className="absolute inset-1 bg-orange-600 rounded-full" />
          </span>
        )}
      </button>

      {/* Chat Panel */}
      <div
        data-testid="chatbot-panel"
        className={`fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-96 h-[560px] max-h-[80vh] bg-zinc-950 border border-zinc-800 shadow-2xl flex flex-col transition-all duration-300 origin-bottom-right ${
          open ? "scale-100 opacity-100" : "scale-90 opacity-0 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="bg-black border-b border-zinc-900 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-600 flex items-center justify-center">
            <Bot size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold uppercase tracking-tight text-white">
              Gensan <span className="text-orange-500">AI</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              Çevrimiçi
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                  m.role === "user"
                    ? "bg-orange-600 text-white"
                    : "bg-zinc-900 text-zinc-100 border border-zinc-800"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 flex gap-1.5">
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        {/* Quick suggestions */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {["Sıfır motorlarınız neler?", "İkinci el var mı?", "Adresiniz nedir?"].map((q) => (
              <button
                key={q}
                onClick={() => { setInput(q); }}
                className="text-[11px] uppercase tracking-widest border border-zinc-800 hover:border-orange-500 hover:text-orange-500 text-zinc-400 px-3 py-1.5 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="border-t border-zinc-900 p-3 flex gap-2">
          <input
            data-testid="chatbot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Mesajınızı yazın..."
            disabled={loading}
            className="flex-1 bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors"
          />
          <button
            data-testid="chatbot-send"
            onClick={send}
            disabled={loading || !input.trim()}
            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white px-4 flex items-center justify-center transition-colors"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
